
import { Question } from './data/types';

export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const getTags = (categoryString: string): string[] => {
    if (!categoryString) return [];
    return categoryString.split('#').map(s => s.trim()).filter(s => s.length > 0);
};

export const downloadFile = (content: string, filename: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const questionsToCSV = (questions: Question[]) => {
  const headers = ['ID', 'FolderID', 'Category', 'Question', 'OptionA', 'OptionB', 'OptionC', 'OptionD', 'CorrectAnswer(0-3)', 'Explanation'];
  const rows = questions.map(q => [
    q.id,
    q.folderId || '',
    q.category,
    q.text,
    q.options[0],
    q.options[1],
    q.options[2],
    q.options[3],
    q.correctAnswerIndex,
    q.explanation
  ].map(field => {
    const stringField = String(field);
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  }).join(','));
  
  return '\ufeff' + [headers.join(','), ...rows].join('\n');
};

export const parseCSVLine = (line: string): string[] => {
    const result = [];
    let start = 0;
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        inQuotes = !inQuotes;
      } else if (line[i] === ',' && !inQuotes) {
        let field = line.substring(start, i);
        if (field.startsWith('"') && field.endsWith('"')) {
          field = field.slice(1, -1).replace(/""/g, '"');
        }
        result.push(field);
        start = i + 1;
      }
    }
    let lastField = line.substring(start);
    if (lastField.startsWith('"') && lastField.endsWith('"')) {
      lastField = lastField.slice(1, -1).replace(/""/g, '"');
    }
    result.push(lastField);
    return result;
};

export const parseCSV = (text: string): Partial<Question>[] => {
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length < 2) return [];

  return lines.slice(1).map(line => {
    const cols = parseCSVLine(line);
    if (cols.length < 10) return null;
    return {
      folderId: cols[1] || null,
      category: cols[2],
      text: cols[3],
      options: [cols[4], cols[5], cols[6], cols[7]],
      correctAnswerIndex: parseInt(cols[8], 10) || 0,
      explanation: cols[9]
    };
  }).filter(q => q !== null) as Partial<Question>[];
};

export const latexToPlainText = (latex: string): string => {
  if (!latex) return "";
  let text = latex;

  // Remove Markdown code blocks if present
  text = text.replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, ''));

  // Standardize LaTeX delimiters \[ \] and $ $
  text = text.replace(/\\\[(.*?)\\\]/g, '$1');
  text = text.replace(/\$([^$]+)\$/g, '$1');

  // Common Symbols Map
  const replacements: Record<string, string> = {
    '\\times': '×', '\\div': '÷', '\\cdot': '·', '\\pm': '±',
    '\\leq': '≤', '\\geq': '≥', '\\neq': '≠', '\\approx': '≈',
    '\\infty': '∞', '\\pi': 'π', '\\alpha': 'α', '\\beta': 'β',
    '\\theta': 'θ', '\\deg': '°', '\\circ': '°', '\\angle': '∠',
    '\\triangle': '△', '\\perp': '⊥', '\\rightarrow': '→', '\\Rightarrow': '⇒',
    '\\because': '∵', '\\therefore': '∴'
  };

  for (const [cmd, char] of Object.entries(replacements)) {
    text = text.split(cmd).join(char);
  }

  // Fractions: \frac{a}{b} -> (a/b)
  // Run loop to handle nested fractions simply
  let oldText = "";
  while (oldText !== text) {
      oldText = text;
      text = text.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1/$2)');
  }

  // Superscripts ^2, ^{...}
  const supers: Record<string, string> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾', 'n': 'ⁿ'
  };
  text = text.replace(/\^\{([0-9+\-=()n]+)\}/g, (_, p1) => 
    p1.split('').map((c: string) => supers[c] || c).join('')
  );
  text = text.replace(/\^([0-9])/g, (_, p1) => supers[p1] || `^${p1}`);

  // Subscripts _{...}
  const subs: Record<string, string> = {
    '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
    '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
  };
  text = text.replace(/_\{([0-9]+)\}/g, (_, p1) => 
    p1.split('').map((c: string) => subs[c] || c).join('')
  );
  text = text.replace(/_([0-9])/g, (_, p1) => subs[p1] || `_${p1}`);

  // Square roots
  text = text.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)');
  text = text.replace(/\\sqrt([0-9a-zA-Z]+)/g, '√$1');

  // Text tag cleanup
  text = text.replace(/\\text\{([^{}]+)\}/g, '$1');
  text = text.replace(/\\ /g, ' ');

  return text;
};
